import { useEffect, useState } from 'react';
import WizardSelectComponent from 'components/v2/epd-wizard-inputs/WizardSelect';
import WizardTextAreaComponent from 'components/v2/epd-wizard-inputs/WizardTextArea';
import WizardTextInputComponent from 'components/v2/epd-wizard-inputs/WizardTextInput';
import WizardTextInputControlledComponent from 'components/v2/epd-wizard-inputs/WizardTextInputControlled';
import { LabelGroup } from 'components/v2/form/styled';
import { ExpandableHelpBox, HelpBox } from 'components/v2/help-boxes';
import { useTranslation } from 'react-i18next';
import { ConversionFactorToMassModel, EPDModel, MaterialPropertyModel, Subtype } from 'services/EpdClient';
import styled from 'styled-components';
import {
  ExpandablePanelHeaderName,
  FieldContainer,
  FieldPanelInput,
  HelpBoldText,
  HorizontalDivider,
} from 'styles/v2/Styles.styled';
import { EPDBasedOnUnitEnum, Option } from 'types/types';
import MaterialPropertiesCopySection from './sections/MaterialPropertiesCopySection';
import DataSourcesCopySection from './sections/DataSourcesCopySection';
import EditDialog from './EditDialog';
import { useDictionaryValues, useReferencePackageVersions15804 } from 'services/api/queries';
import { getOptionsFromDictionary } from 'util/utils';
import { EPD_DICTIONARY_CODES } from 'services/api/constants';
import BiogenicCarbonContentCopySection from './sections/BiogenicCarbonContentCopySection';
import { reactSelectPropsForHtmlLabel } from 'util/reactSelectPropsForHtmlLabel';
import TooltipHelpIcon from 'components/v2/icons/TooltipHelpIcon';
import { MASS_FLOW_PROPERTY_DEFAULT_CONVERSION_FACTOR } from 'constants/constants';
import ToggleButton from '../../compiler/panels/toggleable/ToggleButton';
import { GtinInfo } from 'components/wizard/step2/GtinInfo';
import WizardNumberInput from 'components/v2/epd-wizard-inputs/WizardNumberInput';
import { StatusAttentionIcon } from 'components/v2/icons';
import WizardHTMLEditor from 'components/v2/epd-wizard-inputs/WizardHTMLEditor';

type TProps = {
  isOpened: boolean;
  onHide: any;
  epd: EPDModel;
  onChangeEpd(name: string, value: any): Promise<void>;
  flowPropertiesOptions: Option[];
  materialProperties: MaterialPropertyModel[];
  biogenicCarbonContentUnits?: Option[];
  isConstruction?: boolean;
};

const EditMachineReadableDialog = ({
  isOpened,
  onHide,
  epd,
  onChangeEpd,
  flowPropertiesOptions,
  materialProperties,
  biogenicCarbonContentUnits,
  isConstruction,
}: TProps) => {
  const { t } = useTranslation();
  const referencePackageVersions15804 = useReferencePackageVersions15804().data;

  const selectedFlowPropertyId = epd?.referenceFlow?.flowPropertyId ?? null;
  const selectedFlowConversionFactor = epd?.referenceFlow?.conversionFactor ?? null;
  const isReferenceFlowUnitMass =
    flowPropertiesOptions.find((o) => o.value === selectedFlowPropertyId)?.label === 'Mass (kg)';

  const [selectedFunctionalUnitDescription, setSelectedFunctionalUnitDescription] = useState<string | undefined>(
    epd?.functionalUnitDescription
  );

  const [conversionFactorToMassValue, setConversionFactorToMassValue] = useState<number | null>(
    epd?.conversionFactorToMass?.conversionFactor ?? null
  );

  useEffect(() => {
    setConversionFactorToMassValue(epd?.conversionFactorToMass?.conversionFactor ?? null);
  }, [epd?.conversionFactorToMass?.conversionFactor]);

  const dictionariesQuery = useDictionaryValues();

  const epdBasedOnUnitOptions = getOptionsFromDictionary(dictionariesQuery.data, EPD_DICTIONARY_CODES.EPD_BASED_ON_UNIT);

  const selectedEPDBasedOnUnit = epdBasedOnUnitOptions.find(
    (x) => epd?.epdBasedOnUnit && x.value === Object.keys(EPDBasedOnUnitEnum).indexOf(epd?.epdBasedOnUnit).toString()
  );

  const getSubTypeOptionLabel = (key: string) => {
    switch (key) {
      case Subtype.Specific:
        return 'Specific (single product)';
      case Subtype.Average:
        return 'Average (multiple products)';
      case Subtype.Representative:
        return 'Representative (Sector EPD)';
      case Subtype.Template:
        return 'Template (product not yet on the market)';
      default:
        return '';
    }
  };

  const getSubTypeOptionDisabled = (key: string) => {
    switch (key) {
      case Subtype.Average:
        return epd?.isSectorEPD;
      case Subtype.Specific:
        return epd?.isSectorEPD;
      default:
        return false;
    }
  };

  const productTypeOptions = getOptionsFromDictionary(dictionariesQuery.data, EPD_DICTIONARY_CODES.PRODUCT_TYPE);

  const subtypeOptions: Option[] = (Object.keys(Subtype) as Array<keyof typeof Subtype>).map((key) => ({
    label: getSubTypeOptionLabel(key),
    value: Subtype[key].toString(),
    isDisabled: getSubTypeOptionDisabled(key),
  }));

  const createFlowModel = (name: string | null, flowPropertyId: string | null, conversionFactor: number | null) => {
    return {
      name,
      flowPropertyItem:
        flowPropertyId || conversionFactor
          ? {
              flowPropertyId: flowPropertyId,
              conversionFactor: conversionFactor,
            }
          : null,
    };
  };

  const changeProductFlowUnit = async (
    name: string | null,
    newFlowPropertyId: string | null,
    conversionFactor: number | null
  ) => {
    const newUnitIsMass = flowPropertiesOptions.find((o) => o.value === newFlowPropertyId)?.label === 'Mass (kg)';
    const previousUnitIsMass = flowPropertiesOptions.find((o) => o.value === selectedFlowPropertyId)?.label === 'Mass (kg)';
    if (newUnitIsMass && !previousUnitIsMass) {
      const factor = createConversionFactorToMassModel(MASS_FLOW_PROPERTY_DEFAULT_CONVERSION_FACTOR);
      await onChangeEpd('conversionFactorToMass', factor);
    } else if (!newUnitIsMass && previousUnitIsMass) {
      const factor = createConversionFactorToMassModel(null);
      await onChangeEpd('conversionFactorToMass', factor);
    }

    if (newUnitIsMass) {
      await onChangeEpd('massOfDeclaredUnit', conversionFactor);
    }

    const newValue = createFlowModel(name, newFlowPropertyId, conversionFactor);
    await onChangeEpd('productFlow', newValue);
  };

  const changeProductFlowValue = async (
    name: string | null,
    flowPropertyId: string | null,
    conversionFactor: number | null
  ) => {
    const isMassUnit = flowPropertiesOptions.find((o) => o.value === flowPropertyId)?.label === 'Mass (kg)';
    if (isMassUnit) {
      await onChangeEpd('massOfDeclaredUnit', (conversionFactor as any) === '' ? null : conversionFactor);
    }

    const newValue = createFlowModel(name, flowPropertyId, conversionFactor);
    await onChangeEpd('productFlow', newValue);
  };

  function handleNumericFieldChange(event: any) {
    const { value } = event.target;
    const formattedProductFlowValue = value.replace(/^0+(?=\d)/, ''); // Removes leading zeros, except one before a period
    event.target.value = formattedProductFlowValue;
  }

  const handleNumericFieldKeyDown = (event: any) => {
    const {
      code,
      target: { value },
    } = event;
    const fractionalNumWithOneDigitPattern = /^0\.\d$/;
    if (code === 'Backspace' && fractionalNumWithOneDigitPattern.test(value)) {
      event.target.value = '';
      return;
    }
    if (code !== 'Minus') {
      return;
    }
    event.preventDefault();
  };

  const handleNumericFieldPaste = (event: any) => {
    event.preventDefault();
    const clipboardData = event.clipboardData || window.Clipboard;
    const pastedValue = clipboardData.getData('text');

    if (pastedValue === '0') {
      // Inserts the text at the current caret position
      document.execCommand('insertText', false, '0.01');
      return;
    }

    let formattedProductFlowValue = pastedValue
      .replace(/^0+(?=\d)/, '') // Removes leading zeros, except one before a period
      .replace(/-/g, ''); // Removes minuses

    document.execCommand('insertText', false, formattedProductFlowValue);
  };

  const referencePackageVersions15804Options: Option[] =
    referencePackageVersions15804?.versions.map((r) => ({
      label: r.name,
      value: r.id,
    })) ?? [];

  const changeEPDBasedOnUnit = (epdBasedOnUnit?: any, functionalUnitDescription?: string) => {
    let newValue: any = {
      epdBasedOnUnit: epdBasedOnUnit,
      functionalUnitDescription: functionalUnitDescription,
    };

    return newValue;
  };

  const createConversionFactorToMassModel = (conversionFactor: number | null): ConversionFactorToMassModel => {
    return {
      conversionFactor: conversionFactor,
    };
  };

  const headerHelpBoxTemplate = (options: any, header: string) => {
    return (
      <div className={`${options.className} ${options.collapsed ? 'header-collapsed' : ''}`}>
        <ExpandablePanelHeaderName>
          <HelpBoldText>{header}</HelpBoldText>
        </ExpandablePanelHeaderName>
        <ToggleButton options={options} withMargins />
      </div>
    );
  };

  return (
    <EditDialog header="Information for machine readable EPDs" isOpened={isOpened} onHide={onHide} maximizable>
      <HelpBox>
        Please fill-in the required information below. The filled data will be auto saved and available in the table.
      </HelpBox>
      <FieldPanelInput>
        <FieldContainer>
          <WizardTextInputComponent
            label="Global trade item number"
            name="gtin"
            tooltip={t('epdWizard.wizardStep2.tooltips.gtin')}
            value={epd?.gtin}
            onChanged={onChangeEpd}
            type="number"
          />
          <HelpBox style={{ marginTop: '1rem', display: 'block' }}>
            <GtinInfo />
          </HelpBox>
        </FieldContainer>

        <HorizontalDivider />

        <FieldContainer>
          <WizardSelectComponent
            isClearable={false}
            required={isConstruction}
            label="EPD based on declared or functional unit"
            tooltip={t('epdWizard.wizardStep2.tooltips.basedOnUnit')}
            tooltipPosition="right"
            options={epdBasedOnUnitOptions}
            name="epdBasedOnUnit"
            value={selectedEPDBasedOnUnit}
            onChanged={(name: any, value: any) =>
              onChangeEpd('epdBasedOnUnit', changeEPDBasedOnUnit(value, selectedFunctionalUnitDescription))
            }
          />
        </FieldContainer>
        {epd?.epdBasedOnUnit === EPDBasedOnUnitEnum.FunctionalUnit && (
          <FieldContainer>
            <WizardTextAreaComponent
              label="FUNCTIONAL UNIT DESCRIPTION"
              required={isConstruction}
              value={epd?.functionalUnitDescription}
              state={epd?.functionalUnitDescriptionState?.actionState}
              onChanged={(fieldName: string, value: any) => {
                setSelectedFunctionalUnitDescription(value ?? null);
                onChangeEpd(fieldName, changeEPDBasedOnUnit(EPDBasedOnUnitEnum.FunctionalUnit, value ?? null));
              }}
              name="functionalUnitDescription"
              tooltip={t('epdWizard.wizardStep2.tooltips.funcUnitDesc')}
              tooltipPosition="right"
            />
          </FieldContainer>
        )}

        <HorizontalDivider />

        <FieldContainer>
          <LabelGroup>
            <span style={{ fontWeight: 'bold' }}>
              {t(
                `epdCompiler.step3.lcaSpecificationTab.properties.referenceFlow.title.${
                  selectedEPDBasedOnUnit?.value ??
                  Object.keys(EPDBasedOnUnitEnum).indexOf(EPDBasedOnUnitEnum.DeclaredUnit).toString()
                }`
              )}
            </span>
            <TooltipHelpIcon
              content={t(
                `epdCompiler.step3.lcaSpecificationTab.properties.referenceFlow.tooltip.${
                  selectedEPDBasedOnUnit?.value ??
                  Object.keys(EPDBasedOnUnitEnum).indexOf(EPDBasedOnUnitEnum.DeclaredUnit).toString()
                }`
              )}
              position={'right'}
            />
            {isConstruction && (!epd?.referenceFlow?.name || !selectedFlowPropertyId || !selectedFlowConversionFactor) && (
              <StatusAttentionIcon />
            )}
          </LabelGroup>
          <ExpandableHelpBox
            headerTemplate={(options) => headerHelpBoxTemplate(options, 'What is reference flow?')}
            toggleable
          >
            <span>
              The reference flow is the output of the product. Please provide product flow name, select unit, and provide a
              value (this indicates the declared unit or functional unit). If several reference flows exist, provide the main
              reference/product flow.
              <br />
              <br />
              Example: Cement (CEM II 32.5). Unit: Mass (kg). Value: 1.0
            </span>
          </ExpandableHelpBox>
          <FieldContainer>
            <WizardTextInputComponent
              label="Product flow name"
              required={isConstruction}
              name="productFlow"
              tooltip="Please provide product flow name, select unit, and provide a value."
              value={epd?.referenceFlow?.name ?? ''}
              onChanged={(fieldName: string, value: any) => {
                onChangeEpd(
                  fieldName,
                  createFlowModel(value ? value : '', selectedFlowPropertyId, selectedFlowConversionFactor)
                );
              }}
              tooltipPosition="right"
            />
          </FieldContainer>
        </FieldContainer>
      </FieldPanelInput>
      <FieldContainer>
        <FieldPanel>
          <WizardSelectComponent
            label="Unit"
            tooltip="Indicates the declared unit or functional unit."
            tooltipPosition="right"
            name="productFlow"
            options={flowPropertiesOptions}
            value={flowPropertiesOptions.find((o) => o.value === selectedFlowPropertyId)}
            required={isConstruction}
            onChanged={(name: any, value: any) => {
              changeProductFlowUnit(epd?.referenceFlow?.name ?? null, value ?? null, selectedFlowConversionFactor);
            }}
            {...reactSelectPropsForHtmlLabel}
          />
          <WizardTextInputComponent
            label="Value"
            type="number"
            min="0.1"
            step="0.1"
            required={isConstruction}
            name="productFlow"
            tooltip="Numeric field."
            tooltipPosition="right"
            onKeyDown={handleNumericFieldKeyDown}
            onPaste={handleNumericFieldPaste}
            onChange={handleNumericFieldChange}
            onChanged={(_, value) => {
              changeProductFlowValue(epd?.referenceFlow?.name ?? null, selectedFlowPropertyId, value as unknown as number);
            }}
            value={selectedFlowConversionFactor ?? ''}
          />
        </FieldPanel>
      </FieldContainer>

      <HorizontalDivider />

      <FieldContainer>
        <LabelGroup>
          <span style={{ fontWeight: 'bold' }}>
            {t(`epdCompiler.step3.lcaSpecificationTab.properties.conversionFactor.title`)}
          </span>
          <TooltipHelpIcon
            content={t(`epdCompiler.step3.lcaSpecificationTab.properties.conversionFactor.tooltip`)}
            position={'right'}
          />
          {isConstruction && !conversionFactorToMassValue && <StatusAttentionIcon />}
        </LabelGroup>
        <FieldContainer>
          <FieldPanel>
            <WizardTextInputComponent
              label={t(`epdCompiler.step3.lcaSpecificationTab.properties.conversionFactor.unit`)}
              name="unitId"
              disabled
              value="-"
            />
            <WizardTextInputControlledComponent
              label={t(`epdCompiler.step3.lcaSpecificationTab.properties.conversionFactor.value`)}
              required={isConstruction}
              name="conversionFactorToMass"
              type="number"
              min="0.1"
              step="0.1"
              onChange={(e) => {
                const inputValue = e.target.value;
                const parsedValue =
                  inputValue === undefined || inputValue === null || inputValue === '' ? null : Number(inputValue);
                setConversionFactorToMassValue(parsedValue);
              }}
              onChanged={(_: string, value: number) => {
                onChangeEpd('conversionFactorToMass', createConversionFactorToMassModel(value));
              }}
              value={conversionFactorToMassValue ?? ''}
              placeholder={t('placeholder.typeHere')}
            />
          </FieldPanel>
        </FieldContainer>
      </FieldContainer>

      <HorizontalDivider />

      <FieldContainer>
        <WizardNumberInput
          key={JSON.stringify(epd?.referenceFlow)}
          label={t('epdWizard.wizardStep2.properties.massOfDeclaredUnit.title')}
          tooltip={t('epdWizard.wizardStep2.properties.massOfDeclaredUnit.tooltip')}
          name="massOfDeclaredUnit"
          placeholder="Type number"
          defaultValue={epd?.massOfDeclaredUnit ?? null}
          onChanged={onChangeEpd}
          required={isConstruction}
          disabled={isReferenceFlowUnitMass}
          maxFractionDigits={5}
        />
      </FieldContainer>

      <HorizontalDivider />

      <FieldContainer>
        <WizardSelectComponent
          label={t('epdWizard.wizardStep2.properties.productToDeclare.title')}
          tooltip={t('epdWizard.wizardStep2.properties.productToDeclare.tooltip')}
          tooltipPosition="right"
          options={productTypeOptions}
          name="productType"
          value={epd?.productType ? productTypeOptions.find((o) => o.label === epd.productType) : null}
          onChanged={onChangeEpd}
          isClearable={false}
          required={isConstruction}
        />
      </FieldContainer>

      <HorizontalDivider />

      <BiogenicCarbonContentCopySection
        epd={epd}
        biogenicCarbonContentUnits={biogenicCarbonContentUnits}
        onChangeEpd={onChangeEpd}
        isConstruction={isConstruction}
      />

      <HorizontalDivider />

      <MaterialPropertiesCopySection
        epdMaterialProperties={epd?.materialProperties}
        epdMaterialPropertiesState={epd?.materialPropertiesState}
        materialProperties={materialProperties}
        onSave={(newValues) => onChangeEpd('MaterialProperties', newValues)}
        isConstruction={isConstruction}
      />

      <HorizontalDivider />

      <FieldContainer>
        <WizardTextInputComponent
          label="GEOGRAPHICAL SCOPE DESCRIPTION"
          value={epd?.geographicalDescription}
          onChanged={onChangeEpd}
          name="geographicalDescription"
          tooltip={t('epdWizard.wizardStep2.tooltips.geo')}
          tooltipPosition="right"
        />
        <HelpBox style={{ marginTop: '1rem' }}>{t('epdWizard.wizardStep2.info.geo')}</HelpBox>
      </FieldContainer>

      <HorizontalDivider />

      <FieldContainer>
        <WizardTextAreaComponent
          label="TECHNOLOGY DESCRIPTION INCLUDING BACKGROUND SYSTEM"
          required={isConstruction}
          value={epd?.technologyDescription}
          onChanged={onChangeEpd}
          name="technologyDescription"
          tooltip={t('epdWizard.wizardStep2.tooltips.technology')}
          tooltipPosition="right"
        />
        <ExpandableHelpBox
          style={{ marginTop: '1rem' }}
          headerTemplate={(options) =>
            headerHelpBoxTemplate(options, 'How to describe the technology description including background system?')
          }
          toggleable
        >
          <span>
            Description of the technological characteristics including operating conditions of the product system or process.
            If relevant for the technological representativeness this comprises the technological characteristics of the
            relevant upstream and downstream processes (“background system”) included in the data set. Reference to detailed
            description in EPD-document or to flow diagram can be given, if available.
            <br />
            <br />
            Give information (1-5 sentences) about main technological aspects, to clarify the background of the LCA
            information in the data set. Such as:
            <ul style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>
              <li>Declaration of the main product components and/or materials</li>
              <li>Short description of the manufacturing process</li>
              <li>Information on pre-products or raw materials</li>
              <li>Description of the construction process stage, use stage, and end-of life stage.</li>
            </ul>
            Example: The products considered are Portland slag cement according to DIN EN 197-1. The product consists of
            Portland cement clinker and blast furnace slag as well as sulfate carriers. The blast furnace content is between
            21 and 35 M.-%.
          </span>
        </ExpandableHelpBox>
      </FieldContainer>
      <HorizontalDivider />
      <FieldContainer>
        <WizardTextAreaComponent
          label="TECHNICAL PURPOSE OF PRODUCT OR PROCESS"
          required={isConstruction}
          value={epd?.technicalPurpose}
          onChanged={onChangeEpd}
          name="technicalPurpose"
          tooltip={t('epdWizard.wizardStep2.tooltips.technicalPurpose')}
          tooltipPosition="right"
        />
      </FieldContainer>
      <HorizontalDivider />

      <FieldContainer>
        <WizardSelectComponent
          isClearable={false}
          required={isConstruction}
          label="Subtype for ILCD+EPD"
          tooltip={t('epdWizard.wizardStep2.tooltips.subType')}
          tooltipPosition="right"
          options={subtypeOptions}
          name="subtype"
          value={{ value: epd?.subtype, label: epd?.subtype }}
          onChanged={onChangeEpd}
        />
        <ExpandableHelpBox
          style={{ marginTop: '1rem' }}
          headerTemplate={(options) => headerHelpBoxTemplate(options, 'What is subtype?')}
          toggleable
        >
          <span>
            Indicates the type of data set regarding its representativeness. This field is mandatory for ILCD+EPD format.
            Please note that only one option can be selected.
            <ul style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>
              <li>Specific (single product).</li>
              <li>Average (multiple products).</li>
              <li>Representative (Sector EPD).</li>
              <li>Template (product not yet on the market).</li>
            </ul>
          </span>
        </ExpandableHelpBox>
      </FieldContainer>

      <HorizontalDivider />

      <DataSourcesCopySection
        epd={epd}
        onChangeEpd={onChangeEpd}
        initialDataSources={epd.epdDataSources ?? []}
        onSave={(newValue) => onChangeEpd('DataSourcesProperties', newValue)}
      />

      <FieldContainer>
        <WizardSelectComponent
          label="Version of the EN 15804 reference package"
          options={referencePackageVersions15804Options}
          name="referencePackageVersion15804"
          value={
            epd?.referencePackageVersion15804
              ? referencePackageVersions15804Options.find((o) => o.value === epd.referencePackageVersion15804)
              : null
          }
          onChanged={onChangeEpd}
          isClearable={false}
          required={isConstruction}
        />
      </FieldContainer>

      <FieldContainer>
        <WizardTextInputComponent
          label="Characterisation methods"
          value={epd?.characterisationMethods}
          onChanged={onChangeEpd}
          name="characterisationMethods"
          tooltip={t('epdWizard.wizardStep2.tooltips.characterisationMethods')}
          tooltipPosition={'right'}
        />
      </FieldContainer>

      <HorizontalDivider />

      <FieldContainer>
        <WizardHTMLEditor
          label="Additional information about the underlying LCA-based information"
          value={epd?.lcaBasedDescription}
          onChanged={onChangeEpd}
          name="lcaBasedDescription"
          tooltip='This may include information about assumptions, deviations, justification, cut-off rules, data quality, and allocation. This includes also additional information that PCR and c-PCR(s) may require in given circumstances. This field corresponds to the "General Comment on Data Set" section in the ILCD+EPD format.'
          tooltipPosition={'left'}
          stripPastedStyles={true}
        />
      </FieldContainer>

      <HorizontalDivider />

      <FieldContainer>
        <WizardTextAreaComponent
          label="USE ADVICE FOR THE EPD"
          required={isConstruction}
          value={epd?.useAdvice}
          onChanged={onChangeEpd}
          name="useAdvice"
          tooltip={t('epdWizard.wizardStep2.tooltips.useForDatasets')}
          tooltipPosition={'right'}
        />
        <ExpandableHelpBox
          style={{ marginTop: '1rem' }}
          headerTemplate={(options) => headerHelpBoxTemplate(options, 'What is use advice for the EPD?')}
          toggleable
        >
          <span>{t('epdWizard.wizardStep2.info.useForDatasets')}</span>
        </ExpandableHelpBox>
      </FieldContainer>
    </EditDialog>
  );
};

const FieldPanel = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-column-gap: 30px;
  grid-row-gap: 30px;
  justify-content: start;
  ${(props) => props.theme.media.tablet} {
    grid-template-columns: 1fr 1fr;
  }
  ${(props) => props.theme.media.desktop} {
    grid-template-columns: 1fr 1fr;
  }
`;

const CheckBoxGroup = styled.div`
  text-align: start;
  margin-top: 0.5rem;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  border-radius: 2px;

  & .p-checkbox {
    width: 16px;
    height: 16px;
  }

  & .p-checkbox .p-checkbox-box {
    width: 14px;
    height: 14px;
  }
`;
export default EditMachineReadableDialog;
